/* global globalThis */

/**
 * Pending-action registry — backs the Gateway AI's "AI proposes, user
 * approves" flow for mutations. Tools register a proposed action and return
 * `PENDING_CONFIRMATION` with a token. The popover later consumes the token
 * to retrieve the action and run it via the mutationDispatcher.
 *
 * Why on `window.__GW_AI_PENDING_ACTIONS`: tool `execute()` calls run inside
 * the agent loop, but Approve/Reject clicks happen in React component land.
 * Stashing a Map on `window` is the simplest cross-boundary handoff. Tests
 * fall back to a globalThis Map when there's no window.
 *
 * Per-popover scoping (`popoverId`) prevents a stale token from being
 * approved on a different popover instance after navigation. Popovers MUST
 * call `clearForPopover(popoverId)` on unmount and on AbortController abort.
 */

const STORE_KEY = "__GW_AI_PENDING_ACTIONS";
const DEFAULT_TTL_MS = 5 * 60 * 1000;

function getStore() {
  if (typeof window === "undefined") {
    if (!globalThis.__GW_AI_PENDING_ACTIONS_FALLBACK) {
      globalThis.__GW_AI_PENDING_ACTIONS_FALLBACK = new Map();
    }
    return globalThis.__GW_AI_PENDING_ACTIONS_FALLBACK;
  }
  if (!window[STORE_KEY]) window[STORE_KEY] = new Map();
  return window[STORE_KEY];
}

function generateToken() {
  const a = Math.random().toString(36).slice(2, 10);
  const b = Math.random().toString(36).slice(2, 10);
  return `gact_${a}${b}`;
}

/**
 * Register a proposed action and return its token.
 *
 * @param {Object} opts
 * @param {string} opts.popoverId   Stable id of the originating popover.
 * @param {Object} opts.summary     {title, target, bullets, warnings} for UI.
 * @param {Object} opts.mutation    {kind, ...args} consumed by mutationDispatcher.
 * @param {string} [opts.toolUseId] OpenAI tool_call id, if any.
 * @param {number} [opts.ttlMs]     Expiry window. Default 5 min.
 */
export function register({
  popoverId,
  summary,
  mutation,
  toolUseId = null,
  ttlMs = DEFAULT_TTL_MS,
} = {}) {
  if (!popoverId || typeof popoverId !== "string") {
    throw new Error("pendingActionRegistry.register: popoverId required");
  }
  if (!mutation || typeof mutation !== "object" || !mutation.kind) {
    throw new Error("pendingActionRegistry.register: mutation.kind required");
  }
  const store = getStore();
  const token = generateToken();
  store.set(token, {
    token,
    popoverId,
    actions: [{ summary, mutation, toolUseId }],
    createdAt: Date.now(),
    expiresAt: Date.now() + ttlMs,
    consumed: false,
  });
  return token;
}

/**
 * Read an entry without consuming it. Useful for the card to render in a
 * controlled way (the popover still owns consume/clear).
 */
export function peek(token, popoverId) {
  if (!token || !popoverId) return null;
  const entry = getStore().get(token);
  if (!entry) return null;
  if (entry.popoverId !== popoverId) return null;
  if (entry.consumed) return null;
  if (Date.now() > entry.expiresAt) return null;
  return entry;
}

/**
 * Consume a token. Returns the entry on success; null when not found,
 * mismatched popover, expired, or already consumed. Synchronous so a
 * double-click can't double-execute.
 */
export function consume(token, popoverId) {
  if (!token || !popoverId) return null;
  const store = getStore();
  const entry = store.get(token);
  if (!entry) return null;
  if (entry.popoverId !== popoverId) return null;
  if (entry.consumed) return null;
  if (Date.now() > entry.expiresAt) {
    store.delete(token);
    return null;
  }
  entry.consumed = true;
  store.delete(token);
  return entry;
}

/**
 * Drop all entries belonging to a popover. Call on unmount + on user-initiated
 * abort so an aborted turn doesn't leave dangling tokens that could be
 * approved later.
 */
export function clearForPopover(popoverId) {
  if (!popoverId) return;
  const store = getStore();
  for (const [token, entry] of store.entries()) {
    if (entry.popoverId === popoverId) store.delete(token);
  }
}
